/**
 * @remarks Playground Schema 校验工具
 * @author ickeep <i@ickeep.com>
 * @module @yimoka/designable-playground
 */

import type { ITreeNode } from '@yimoka/designable-core';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object'
  && value !== null
  && !Array.isArray(value)
);

/** Formily Schema 中以字段映射承载子节点的结构键。 */
export const FORMILY_SCHEMA_RECORD_CHILD_KEYS = [
  '$defs',
  'definitions',
  'dependentSchemas',
  'patternProperties',
  'properties',
  'x-additional-schema',
] as const;

/** Formily Schema 中只承载一个子节点的结构键。 */
export const FORMILY_SCHEMA_SINGLE_CHILD_KEYS = [
  'additionalItems',
  'additionalProperties',
  'contains',
  'else',
  'if',
  'not',
  'then',
] as const;

/** Formily Schema 中以数组承载子节点的结构键。 */
export const FORMILY_SCHEMA_ARRAY_CHILD_KEYS = [
  'allOf',
  'anyOf',
  'oneOf',
  'prefixItems',
] as const;

/** Formily Schema 结构遍历器收到的节点和稳定路径。 */
export interface IFormilySchemaVisit {
  node: Record<string, unknown>;
  path: string;
}

/**
 * 只遍历 Formily Schema 的结构化子节点。
 *
 * 组件 props 中也可能包含 properties/items 等普通业务数据，因此必须从已知
 * Schema 结构键向下走，不能对所有对象做无差别递归。
 */
export const visitFormilySchemaNodes = (
  value: unknown,
  path: string,
  visitor: (visit: IFormilySchemaVisit) => void,
) => {
  if (!isRecord(value)) return;

  visitor({
    node: value,
    path,
  });

  FORMILY_SCHEMA_RECORD_CHILD_KEYS.forEach((key) => {
    const children = value[key];
    if (!isRecord(children)) return;
    Object.entries(children).forEach(([childKey, child]) => {
      visitFormilySchemaNodes(child, `${path}.${key}.${childKey}`, visitor);
    });
  });

  FORMILY_SCHEMA_SINGLE_CHILD_KEYS.forEach((key) => {
    const child = value[key];
    if (isRecord(child)) {
      visitFormilySchemaNodes(child, `${path}.${key}`, visitor);
    }
  });

  FORMILY_SCHEMA_ARRAY_CHILD_KEYS.forEach((key) => {
    const children = value[key];
    if (!Array.isArray(children)) return;
    children.forEach((child, index) => {
      visitFormilySchemaNodes(child, `${path}.${key}[${index}]`, visitor);
    });
  });

  const { items } = value;
  if (Array.isArray(items)) {
    items.forEach((item, index) => {
      visitFormilySchemaNodes(item, `${path}.items[${index}]`, visitor);
    });
  } else if (isRecord(items)) {
    visitFormilySchemaNodes(items, `${path}.items`, visitor);
  }
};

export type SchemaValidationIssueSeverity = 'error' | 'warning';

export interface ISchemaValidationIssue {
  /** 问题编码，便于宿主做 i18n 或埋点聚合 */
  code: string;
  /** 问题描述 */
  message: string;
  /** Schema 路径 */
  path: string;
  /** 严重级别 */
  severity: SchemaValidationIssueSeverity;
  /** 关联组件名 */
  componentName?: string;
}

export interface ISchemaValidationResult {
  /** 是否没有 error 级问题 */
  valid: boolean;
  /** 诊断问题列表 */
  issues: ISchemaValidationIssue[];
}

export interface ITreeSchemaValidationOptions {
  /** 已注册组件名；传入后会校验画布节点是否引用未注册组件 */
  knownComponentNames?: Iterable<string>;
  /** 可编辑组件名；传入后会校验画布节点是否引用了缺少 Behavior 的组件 */
  editableComponentNames?: Iterable<string>;
  /** 是否诊断危险 HTML 和高风险表达式；默认开启 */
  includeSecurityDiagnostics?: boolean;
  /** 根节点组件名 */
  rootComponentName?: string;
}

export interface IDesignableSchemaMigrationOptions {
  /** 目标 Schema 版本；默认迁移到当前运行时支持版本 */
  targetVersion?: number;
  /** 诊断路径 */
  path?: string;
}

export interface IDesignableSchemaMigrationResult<T = unknown> {
  /** 迁移后的 Schema 副本 */
  schema: T;
  /** 输入 Schema 显式声明的版本；未声明时按当前版本兼容处理 */
  fromVersion: number;
  /** 目标版本 */
  toVersion: number;
  /** 是否执行过真实版本迁移动作 */
  migrated: boolean;
  /** 非阻断兼容诊断，例如未来版本提示 */
  issues: ISchemaValidationIssue[];
}

export const CURRENT_DESIGNABLE_SCHEMA_VERSION = 1;

export const DESIGNABLE_SCHEMA_VERSION_PROP = 'x-designable-schema-version';

export const RUNTIME_DOCUMENT_SIDECAR_PROP = 'x-designable-runtime-sidecar-v1';

export const UNSAFE_SCHEMA_OBJECT_KEYS = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

export const isSafeSchemaObjectKey = (key: string) => !UNSAFE_SCHEMA_OBJECT_KEYS.has(key);

/** 解码 JSON Pointer 单段；非法 URI 编码或非法波浪号转义保持为未解析。 */
export const decodeJsonPointerSegment = (segment: string) => {
  try {
    const decoded = decodeURIComponent(segment);
    if (/~(?:[^01]|$)/u.test(decoded)) return undefined;
    return decoded.replace(/~1/gu, '/').replace(/~0/gu, '~');
  } catch {
    return undefined;
  }
};

export const assertSafeSchemaObjectKeys = (
  value: unknown,
  path = 'Schema',
) => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      assertSafeSchemaObjectKeys(item, `${path}.${index}`);
    });
    return;
  }

  if (!isRecord(value)) return;

  Object.entries(value).forEach(([key, item]) => {
    const nextPath = `${path}.${key}`;
    if (!isSafeSchemaObjectKey(key)) {
      throw new Error(`Schema 包含不安全字段路径：${nextPath}`);
    }
    assertSafeSchemaObjectKeys(item, nextPath);
  });
};

export const isUnsafeSchemaObjectKeyError = (error: unknown) => (
  error instanceof Error
  && error.message.startsWith('Schema 包含不安全字段路径：')
);

// 解析 Schema JSON，并在对象进入设计器前拒绝原型链危险键。
export const parseSchemaJSON = (text: string) => {
  const schema = JSON.parse(text) as unknown;
  assertSafeSchemaObjectKeys(schema);
  return schema;
};

export const FORMILY_SCHEMA_PROP_NAMES = new Set([
  '$anchor',
  '$comment',
  '$defs',
  '$id',
  '$ref',
  '$schema',
  'additionalItems',
  'additionalProperties',
  'allOf',
  'anyOf',
  'contains',
  'const',
  'contentEncoding',
  'contentMediaType',
  'contentSchema',
  'default',
  'definitions',
  'dependencies',
  'dependentRequired',
  'dependentSchemas',
  'deprecated',
  'description',
  'discriminator',
  'else',
  'enum',
  'enumNames',
  'examples',
  'exclusiveMaximum',
  'exclusiveMinimum',
  'format',
  'if',
  'maxContains',
  'maxItems',
  'maxLength',
  'maxProperties',
  'maximum',
  'minContains',
  'minItems',
  'minLength',
  'minProperties',
  'minimum',
  'multipleOf',
  'not',
  'nullable',
  'oneOf',
  'pattern',
  'patternProperties',
  'prefixItems',
  'propertyNames',
  'readOnly',
  'required',
  'then',
  'unevaluatedItems',
  'unevaluatedProperties',
  'uniqueItems',
  'writeOnly',
]);

export const RESERVED_FORMILY_SCHEMA_PROP_NAMES = new Set([
  'items',
  'properties',
  'title',
  'type',
  'x-component',
  'x-component-props',
  'x-designable-id',
  'x-index',
]);

export const RENDER_ONLY_SCHEMA_PROP_NAMES = new Set([
  'x-component',
  'x-component-props',
  'x-designable-id',
  'x-index',
]);

const RISKY_EXPRESSION_PATTERN = /\{\{[\s\S]*(?:\b(?:document|eval|fetch|Function|globalThis|localStorage|sessionStorage|window|XMLHttpRequest)\b|import\s*\(|new\s+Function)[\s\S]*\}\}/i;

const UNSAFE_HTML_PATTERN = /<\s*(?:script|iframe|object|embed)\b|javascript\s*:|\son[a-z]+\s*=/i;

const EVENT_HANDLER_PROP_PATTERN = /^on[A-Z]/;

export const INTERNAL_DESIGNABLE_SCHEMA_PROP_NAMES = new Set([
  'x-designable-formily-package',
  'x-designable-root-form',
  'x-designable-root-schema',
  'x-designable-root-schema-form',
  RUNTIME_DOCUMENT_SIDECAR_PROP,
  'x-designable-schema-key',
  'x-designable-schema-type',
  DESIGNABLE_SCHEMA_VERSION_PROP,
  'x-designable-state-hidden-from-visible',
  'x-designable-scalar-array-item',
  'x-designable-synthetic-array-item',
]);

export const COMPONENT_SCHEMA_PROP_NAMES_AS_COMPONENT_PROPS: Record<string, Set<string>> = {
  Alert: new Set(['description']),
  ColorPicker: new Set(['format']),
  DatePicker: new Set(['format']),
  DateRangePicker: new Set(['format']),
  Empty: new Set(['description']),
  Statistic: new Set(['title']),
  TimePicker: new Set(['format']),
  TimeRangePicker: new Set(['format']),
};

export const isComponentSchemaPropName = (
  componentName: string | undefined,
  key: string,
) => (
  Boolean(componentName && COMPONENT_SCHEMA_PROP_NAMES_AS_COMPONENT_PROPS[componentName]?.has(key))
);

export const isSchemaMetadataPropName = (
  key: string,
  componentName?: string,
) => (
  !INTERNAL_DESIGNABLE_SCHEMA_PROP_NAMES.has(key)
  && !isComponentSchemaPropName(componentName, key)
  && (
    FORMILY_SCHEMA_PROP_NAMES.has(key)
    || (key.startsWith('x-') && !RESERVED_FORMILY_SCHEMA_PROP_NAMES.has(key))
  )
);

export const isSchemaOnlyRenderPropName = (
  key: string,
  componentName?: string,
) => {
  if (isComponentSchemaPropName(componentName, key)) return false;
  return (
    FORMILY_SCHEMA_PROP_NAMES.has(key)
    || RENDER_ONLY_SCHEMA_PROP_NAMES.has(key)
    || INTERNAL_DESIGNABLE_SCHEMA_PROP_NAMES.has(key)
    || key.startsWith('x-')
  );
};

/**
 * 校验默认 TreeNode Schema。
 *
 * 在无 transformer 模式下，导入与 JSON 编辑都必须先完整校验再应用，
 * 避免 TreeNode.from 在非法 children 上静默清空画布。
 */
const validateTreeSchemaBase: (schema: unknown) => asserts schema is ITreeNode = (schema) => {
  if (!isRecord(schema) || typeof schema.componentName !== 'string') {
    throw new Error('Schema 必须包含字符串类型的 componentName');
  }
};

const validateTreeSchemaProps = (schema: ITreeNode) => {
  if (schema.props !== undefined && !isRecord(schema.props)) {
    throw new Error('Schema props 必须是对象');
  }

  if (schema.sourceName !== undefined && typeof schema.sourceName !== 'string') {
    throw new Error('Schema sourceName 必须是字符串');
  }

  if (schema.hidden !== undefined && typeof schema.hidden !== 'boolean') {
    throw new Error('Schema hidden 必须是布尔值');
  }
};

const validateTreeSchemaId = (schema: ITreeNode, seenIds: Set<string>) => {
  if (schema.id === undefined) return;

  if (typeof schema.id !== 'string') {
    throw new Error('Schema id 必须是字符串');
  }
  if (seenIds.has(schema.id)) {
    throw new Error(`Schema id 不能重复：${schema.id}`);
  }
  seenIds.add(schema.id);
};

const validateTreeSchemaChildren = (schema: ITreeNode, seenIds: Set<string>) => {
  if (schema.children === undefined) return;

  if (!Array.isArray(schema.children)) {
    throw new Error('Schema children 必须是数组');
  }
  schema.children.forEach(child => validateTreeSchemaNode(child, seenIds));
};

const validateTreeSchemaNode: (schema: unknown, seenIds: Set<string>) => asserts schema is ITreeNode = (
  schema,
  seenIds,
) => {
  validateTreeSchemaBase(schema);
  validateTreeSchemaProps(schema);
  validateTreeSchemaId(schema, seenIds);
  validateTreeSchemaChildren(schema, seenIds);
};

export const assertTreeSchema: (schema: unknown) => asserts schema is ITreeNode = (
  schema,
) => {
  assertSafeSchemaObjectKeys(schema);
  validateTreeSchemaNode(schema, new Set<string>());
};

const createValidationResult = (issues: ISchemaValidationIssue[]): ISchemaValidationResult => ({
  issues,
  valid: !issues.some(issue => issue.severity === 'error'),
});

const getSchemaVersionValue = (schema: unknown) => (
  isRecord(schema) ? schema[DESIGNABLE_SCHEMA_VERSION_PROP] : undefined
);

const isPositiveInteger = (value: unknown): value is number => (
  typeof value === 'number'
  && Number.isInteger(value)
  && value > 0
);

export const getDesignableSchemaVersion = (schema: unknown): number | undefined => {
  const version = getSchemaVersionValue(schema);
  return isPositiveInteger(version) ? version : undefined;
};

type DesignableSchemaMigrator = (schema: unknown) => unknown;

const DESIGNABLE_SCHEMA_MIGRATORS: Partial<Record<number, DesignableSchemaMigrator>> = {};

const cloneSafeDesignableSchema = <T, >(schema: T): T => {
  const schemaText = stringifySchemaForExport(schema, 0);
  return parseSchemaJSON(schemaText) as T;
};

export const validateDesignableSchemaVersion = (
  schema: unknown,
  path = `Schema.${DESIGNABLE_SCHEMA_VERSION_PROP}`,
): ISchemaValidationIssue[] => {
  const version = getSchemaVersionValue(schema);
  if (version === undefined) return [];

  if (!isPositiveInteger(version)) {
    return [{
      code: 'schema_version_invalid',
      message: `${DESIGNABLE_SCHEMA_VERSION_PROP} 必须是正整数`,
      path,
      severity: 'error',
    }];
  }

  if (version > CURRENT_DESIGNABLE_SCHEMA_VERSION) {
    return [{
      code: 'schema_version_future',
      message: `Schema 版本 ${version} 高于当前支持版本 ${CURRENT_DESIGNABLE_SCHEMA_VERSION}，生产保存前建议确认兼容性`,
      path,
      severity: 'warning',
    }];
  }

  if (version < CURRENT_DESIGNABLE_SCHEMA_VERSION) {
    return [{
      code: 'schema_version_legacy',
      message: `Schema 版本 ${version} 低于当前支持版本 ${CURRENT_DESIGNABLE_SCHEMA_VERSION}，已按兼容模式处理`,
      path,
      severity: 'warning',
    }];
  }

  return [];
};

export const migrateDesignableSchema = <T = unknown, >(
  schema: T,
  options: IDesignableSchemaMigrationOptions = {},
): IDesignableSchemaMigrationResult<T> => {
  const targetVersion = options.targetVersion ?? CURRENT_DESIGNABLE_SCHEMA_VERSION;
  if (!isPositiveInteger(targetVersion)) {
    throw new Error('目标 Schema 版本必须是正整数');
  }

  const nextSchema = cloneSafeDesignableSchema(schema);
  const path = options.path ?? `Schema.${DESIGNABLE_SCHEMA_VERSION_PROP}`;
  const issues = validateDesignableSchemaVersion(nextSchema, path);
  const blockingIssue = issues.find(issue => issue.severity === 'error');
  if (blockingIssue) {
    throw new Error(blockingIssue.message);
  }

  const sourceVersion = getDesignableSchemaVersion(nextSchema) ?? CURRENT_DESIGNABLE_SCHEMA_VERSION;
  if (sourceVersion > targetVersion) {
    return {
      fromVersion: sourceVersion,
      issues,
      migrated: false,
      schema: nextSchema,
      toVersion: targetVersion,
    };
  }

  let migratedSchema: unknown = nextSchema;
  for (let version = sourceVersion; version < targetVersion; version += 1) {
    const migrator = DESIGNABLE_SCHEMA_MIGRATORS[version];
    if (!migrator) {
      throw new Error(`缺少 Schema v${version} 到 v${version + 1} 的迁移器`);
    }
    migratedSchema = migrator(migratedSchema);
  }

  return {
    fromVersion: sourceVersion,
    issues,
    migrated: sourceVersion < targetVersion,
    schema: migratedSchema as T,
    toVersion: targetVersion,
  };
};

const getKnownComponentNameSet = (options?: ITreeSchemaValidationOptions) => {
  if (!options?.knownComponentNames) return undefined;

  const names = new Set(options.knownComponentNames);
  if (options.rootComponentName) {
    names.add(options.rootComponentName);
  }
  names.add('Root');
  return names;
};

const getEditableComponentNameSet = (options?: ITreeSchemaValidationOptions) => {
  if (!options?.editableComponentNames) return undefined;

  const names = new Set(options.editableComponentNames);
  if (options.rootComponentName) {
    names.add(options.rootComponentName);
  }
  names.add('Root');
  return names;
};

const pushIssue = (
  issues: ISchemaValidationIssue[],
  issue: ISchemaValidationIssue,
) => {
  issues.push(issue);
};

const getRecordComponentName = (record: Record<string, unknown>) => {
  if (typeof record.componentName === 'string') return record.componentName;
  if (typeof record['x-component'] === 'string') return record['x-component'];
  return undefined;
};

const getRecordComponentProps = (
  record: Record<string, unknown>,
) => {
  if (isRecord(record.props)) return {
    pathKey: 'props',
    value: record.props,
  };
  if (isRecord(record['x-component-props'])) return {
    pathKey: 'x-component-props',
    value: record['x-component-props'],
  };
  return undefined;
};

const pushUnsafeHtmlIssue = (
  issues: ISchemaValidationIssue[],
  path: string,
  componentName?: string,
) => {
  pushIssue(issues, {
    code: 'unsafe_html_content',
    componentName,
    message: 'HTML 内容包含不安全脚本、事件属性或协议',
    path,
    severity: 'error',
  });
};

const pushRiskyExpressionIssue = (
  issues: ISchemaValidationIssue[],
  path: string,
  componentName?: string,
) => {
  pushIssue(issues, {
    code: 'risky_expression',
    componentName,
    message: '表达式引用了浏览器全局对象或动态执行能力，生产保存前建议人工确认',
    path,
    severity: 'warning',
  });
};

const pushEventExpressionIssue = (
  issues: ISchemaValidationIssue[],
  path: string,
  componentName?: string,
) => {
  pushIssue(issues, {
    code: 'event_expression_handler',
    componentName,
    message: '事件属性使用表达式，生产保存前建议确认执行边界',
    path,
    severity: 'warning',
  });
};

const validateSecurityStringValue = (
  value: string,
  issues: ISchemaValidationIssue[],
  path: string,
  key: string,
  componentName?: string,
) => {
  if (
    ((componentName === 'HTMLContent' && key === 'value') || key === '__html')
    && UNSAFE_HTML_PATTERN.test(value)
  ) {
    pushUnsafeHtmlIssue(issues, path, componentName);
  }

  if (RISKY_EXPRESSION_PATTERN.test(value)) {
    pushRiskyExpressionIssue(issues, path, componentName);
  }

  if (EVENT_HANDLER_PROP_PATTERN.test(key) && value.trim()) {
    pushEventExpressionIssue(issues, path, componentName);
  }
};

const validateSchemaSecurityNode = (
  value: unknown,
  issues: ISchemaValidationIssue[],
  path: string,
  inheritedComponentName?: string,
) => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      validateSchemaSecurityNode(item, issues, `${path}.${index}`, inheritedComponentName);
    });
    return;
  }

  if (!isRecord(value)) return;

  const componentName = getRecordComponentName(value) ?? inheritedComponentName;
  const componentProps = getRecordComponentProps(value);
  if (componentProps) {
    Object.entries(componentProps.value).forEach(([key, item]) => {
      const propPath = `${path}.${componentProps.pathKey}.${key}`;
      if (typeof item === 'string') {
        validateSecurityStringValue(item, issues, propPath, key, componentName);
      }
      validateSchemaSecurityNode(item, issues, propPath, componentName);
    });
  }

  Object.entries(value).forEach(([key, item]) => {
    if (key === 'props' || key === 'x-component-props') return;

    const nextPath = `${path}.${key}`;
    if (typeof item === 'string') {
      validateSecurityStringValue(item, issues, nextPath, key, componentName);
    }
    validateSchemaSecurityNode(item, issues, nextPath, componentName);
  });
};

export const validateSchemaSecurity = (
  schema: unknown,
  path = 'Schema',
): ISchemaValidationIssue[] => {
  const issues: ISchemaValidationIssue[] = [];
  validateSchemaSecurityNode(schema, issues, path);
  return issues;
};

const getDiagnosticComponentName = (schema: Record<string, unknown>) => (
  typeof schema.componentName === 'string' && schema.componentName.trim()
    ? schema.componentName
    : undefined
);

const validateDiagnosticComponentName = (
  schema: Record<string, unknown>,
  issues: ISchemaValidationIssue[],
  path: string,
  knownComponentNames?: Set<string>,
  editableComponentNames?: Set<string>,
) => {
  const componentName = getDiagnosticComponentName(schema);

  if (!componentName) {
    pushIssue(issues, {
      code: 'component_name_required',
      message: 'Schema 节点必须包含非空字符串 componentName',
      path: `${path}.componentName`,
      severity: 'error',
    });
  } else if (knownComponentNames && !knownComponentNames.has(componentName)) {
    pushIssue(issues, {
      code: 'component_not_registered',
      componentName,
      message: `组件未注册：${componentName}`,
      path: `${path}.componentName`,
      severity: 'error',
    });
  } else if (editableComponentNames && !editableComponentNames.has(componentName)) {
    pushIssue(issues, {
      code: 'component_behavior_missing',
      componentName,
      message: `组件缺少 Behavior，无法在设计视图中编辑：${componentName}`,
      path: `${path}.componentName`,
      severity: 'error',
    });
  }

  return componentName;
};

const validateDiagnosticNodeId = (
  schema: Record<string, unknown>,
  issues: ISchemaValidationIssue[],
  seenIds: Set<string>,
  path: string,
  componentName?: string,
) => {
  if (schema.id === undefined) return;

  if (typeof schema.id !== 'string' || !schema.id.trim()) {
    pushIssue(issues, {
      code: 'node_id_invalid',
      componentName,
      message: 'Schema id 必须是非空字符串',
      path: `${path}.id`,
      severity: 'error',
    });
    return;
  }

  if (seenIds.has(schema.id)) {
    pushIssue(issues, {
      code: 'node_id_duplicated',
      componentName,
      message: `Schema id 不能重复：${schema.id}`,
      path: `${path}.id`,
      severity: 'error',
    });
    return;
  }

  seenIds.add(schema.id);
};

const validateDiagnosticNodeProps = (
  schema: Record<string, unknown>,
  issues: ISchemaValidationIssue[],
  path: string,
  componentName?: string,
) => {
  if (schema.props === undefined || isRecord(schema.props)) return;

  pushIssue(issues, {
    code: 'node_props_invalid',
    componentName,
    message: 'Schema props 必须是对象',
    path: `${path}.props`,
    severity: 'error',
  });
};

const validateDiagnosticScalarFields = (
  schema: Record<string, unknown>,
  issues: ISchemaValidationIssue[],
  path: string,
  componentName?: string,
) => {
  if (schema.sourceName !== undefined && typeof schema.sourceName !== 'string') {
    pushIssue(issues, {
      code: 'node_source_name_invalid',
      componentName,
      message: 'Schema sourceName 必须是字符串',
      path: `${path}.sourceName`,
      severity: 'error',
    });
  }

  if (schema.hidden !== undefined && typeof schema.hidden !== 'boolean') {
    pushIssue(issues, {
      code: 'node_hidden_invalid',
      componentName,
      message: 'Schema hidden 必须是布尔值',
      path: `${path}.hidden`,
      severity: 'error',
    });
  }
};

const validateDiagnosticChildren = (
  schema: Record<string, unknown>,
  issues: ISchemaValidationIssue[],
  seenIds: Set<string>,
  path: string,
  knownComponentNames?: Set<string>,
  editableComponentNames?: Set<string>,
  componentName?: string,
) => {
  if (schema.children === undefined) return;

  if (!Array.isArray(schema.children)) {
    pushIssue(issues, {
      code: 'node_children_invalid',
      componentName,
      message: 'Schema children 必须是数组',
      path: `${path}.children`,
      severity: 'error',
    });
    return;
  }

  schema.children.forEach((child, index) => {
    validateTreeSchemaNodeForDiagnostics(
      child,
      issues,
      seenIds,
      `${path}.children.${index}`,
      knownComponentNames,
      editableComponentNames,
    );
  });
};

const validateTreeSchemaNodeForDiagnostics = (
  schema: unknown,
  issues: ISchemaValidationIssue[],
  seenIds: Set<string>,
  path: string,
  knownComponentNames?: Set<string>,
  editableComponentNames?: Set<string>,
) => {
  if (!isRecord(schema)) {
    pushIssue(issues, {
      code: 'tree_node_not_object',
      message: 'Schema 节点必须是对象',
      path,
      severity: 'error',
    });
    return;
  }

  const componentName = validateDiagnosticComponentName(
    schema,
    issues,
    path,
    knownComponentNames,
    editableComponentNames,
  );
  validateDiagnosticNodeId(schema, issues, seenIds, path, componentName);
  validateDiagnosticNodeProps(schema, issues, path, componentName);
  validateDiagnosticScalarFields(schema, issues, path, componentName);
  validateDiagnosticChildren(
    schema,
    issues,
    seenIds,
    path,
    knownComponentNames,
    editableComponentNames,
    componentName,
  );
};

export const validateTreeSchema = (
  schema: unknown,
  options?: ITreeSchemaValidationOptions,
): ISchemaValidationResult => {
  const issues: ISchemaValidationIssue[] = [];

  try {
    assertSafeSchemaObjectKeys(schema);
  } catch (error) {
    pushIssue(issues, {
      code: 'unsafe_schema_key',
      message: error instanceof Error ? error.message : 'Schema 包含不安全字段',
      path: 'Schema',
      severity: 'error',
    });
    return createValidationResult(issues);
  }

  validateTreeSchemaNodeForDiagnostics(
    schema,
    issues,
    new Set<string>(),
    'Schema',
    getKnownComponentNameSet(options),
    getEditableComponentNameSet(options),
  );
  issues.push(...validateDesignableSchemaVersion(schema));
  if (options?.includeSecurityDiagnostics !== false) {
    issues.push(...validateSchemaSecurity(schema));
  }

  return createValidationResult(issues);
};

export const stringifySchemaForExport = (schema: unknown, space = 2) => {
  const schemaText = JSON.stringify(schema, null, space);
  if (typeof schemaText !== 'string') {
    throw new Error('当前画布 Schema 为空，无法导出 JSON');
  }
  return schemaText;
};

export const createSchemaFingerprint = (getSchema: () => unknown): string | undefined => {
  try {
    return stringifySchemaForExport(getSchema(), 0);
  } catch {
    return undefined;
  }
};
